import * as THREE from 'three';

// ============================================================
//  Процедурные текстуры материалов карты.
//
//  ⚠️ ЗАЧЕМ ВООБЩЕ. Карта была раскрашена по ВЫСОТЕ шестью оттенками одного
//  песочного цвета — ящик, стена и пол на одном уровне были одного тона.
//  Жалоба владельца была не про красоту, а про игру: «цвета сливаются, не
//  видно персонажей». Материал (дерево / песок / бетон / камень) даёт и
//  разнообразие тона, и разную мелкую фактуру — на ней глаз ловит силуэт.
//
//  ⚠️ ПОЧЕМУ ВСЁ СЧИТАЕТСЯ, А НЕ ГРУЗИТСЯ КАРТИНКАМИ. Три причины, и ни одна
//  не про экономию места:
//    1. Требование владельца — не тащить ассеты извне;
//    2. текстуры исходной de_dust2 брать НЕЛЬЗЯ (IP Valve, см. md/graphics.md):
//       из BSP мы берём только ИМЕНА текстур, чтобы понять, где дерево, а где
//       песчаник, — но рисуем своё;
//    3. считанные на старте 8 слоёв по 256×256 в один канал — это 512 КБ
//       видеопамяти и ноль сетевых запросов.
//
//  ⚠️ ОДИН КАНАЛ (RedFormat), А НЕ ЦВЕТ. Текстура серая и работает
//  МНОЖИТЕЛЕМ яркости, а цвет приходит вершинами из `map.json::palette`.
//  Это не полумера: кадр здесь упирается в пиксели (баг-урок «FPS упал с 60
//  до 20» в md/bug-lessons.md), а RGB стоил бы втрое больше пропускной
//  способности памяти на каждой выборке ради оттенков, которых на камне
//  и песке никто не различит. Заодно один и тот же слой можно красить в
//  разные цвета — так дерево ящика и дерево ворот отличаются тоном, а не
//  отдельным слоем.
//
//  ⚠️ МАССИВ ТЕКСТУР (`DataArrayTexture`), А НЕ АТЛАС. У карты ОДИН материал
//  на все чанки (иначе меши размножатся по материалам и вернётся проблема
//  вызовов отрисовки — см. world.js). Значит все восемь картинок должны жить
//  в одной привязке. Атлас для этого не годится: тайлинг внутри ячейки атласа
//  делается через `fract`, а от разрыва производных на швах GPU выбирает
//  случайный мип-уровень — по стене идут мыльные полосы. У массива слои
//  независимы, `RepeatWrapping` и мипы работают честно.
//  Цена: нужен WebGL2 (`sampler2DArray`). Фолбэк — в world.js.
// ============================================================

/** Сторона каждого слоя. 256 — power-of-two (иначе не будет мипов и wrap). */
const SIZE = 256;

/**
 * Байт, который означает множитель ровно 1.0.
 *
 * Текстура — множитель яркости, и «средний» пиксель обязан ничего не менять,
 * иначе базовый цвет из палитры перестаёт быть тем цветом, который видно.
 * 180 из 255 оставляет запас вверх (до ×1.42) — блики на сколах ярче базы.
 * ⚠️ Зеркало множителя `1.4167` в шейдере (world.js) — менять только парой.
 */
const MID = 180;

/**
 * Размер тайла в МЕТРАХ на каждый материал.
 *
 * ⚠️ Живёт здесь, а применяется в world.js: UV считаются на процессоре при
 * сборке геометрии (`uv = мировые координаты / TILE`), поэтому масштаб ничего
 * не стоит в кадре. Крутить размер кладки/досок — здесь, шейдер не трогать.
 */
export const MAT_TILE = {
  sand: 2.5,   // песчинка мелкая, тайл крупный — иначе видно повтор
  road: 2.5,   // 5×5 плит на тайл
  crete: 3.0,
  wall: 2.0,   // 4 ряда кладки на тайл → кирпич 0.5 м высотой
  trim: 1.0,   // профиль карниза читается только вблизи
  rock: 3.0,
  wood: 1.2,   // 4 доски на тайл → доска 0.3 м
  metal: 1.0,
};

// ------------------------------------------------------------------ шум
//
// Всё ниже — значение-шум (value noise) на периодической решётке.
// ⚠️ ПЕРИОДИЧНОСТЬ ОБЯЗАТЕЛЬНА: текстура тайлится по обеим осям, и решётка,
// не замкнутая по модулю, даёт прямой шов через всю стену. Ровно эти же
// грабли записаны в sky.js про облака — там шов шёл по небу.

function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Решётка n×n случайных чисел. */
function lattice(n, rnd) {
  const a = new Float32Array(n * n);
  for (let i = 0; i < a.length; i++) a[i] = rnd();
  return a;
}

const fade = (t) => t * t * (3 - 2 * t);

/** Значение шума в точке (x, y), заданной В ЯЧЕЙКАХ решётки. Периодичен по n. */
function noise(lat, n, x, y) {
  const xi = Math.floor(x), yi = Math.floor(y);
  const fx = fade(x - xi), fy = fade(y - yi);
  const x0 = ((xi % n) + n) % n, y0 = ((yi % n) + n) % n;
  const x1 = (x0 + 1) % n, y1 = (y0 + 1) % n;
  const a = lat[y0 * n + x0], b = lat[y0 * n + x1];
  const c = lat[y1 * n + x0], d = lat[y1 * n + x1];
  return (a + (b - a) * fx) * (1 - fy) + (c + (d - c) * fx) * fy;
}

/**
 * Фрактальный шум: несколько октав удвоенной частоты.
 * `base` — сколько ячеек решётки на всю текстуру в первой октаве.
 */
function fbm(u, v, lats, base, oct) {
  let sum = 0, amp = 1, norm = 0, f = base;
  for (let o = 0; o < oct; o++) {
    sum += amp * noise(lats[o], f, u * f, v * f);
    norm += amp; amp *= 0.5; f *= 2;
  }
  return sum / norm;
}

/** Набор решёток под fbm: у каждой октавы своя, размером в её частоту. */
function fbmLattices(base, oct, rnd) {
  const out = [];
  for (let o = 0, f = base; o < oct; o++, f *= 2) out.push(lattice(f, rnd));
  return out;
}

// ------------------------------------------------- рисовалки материалов
//
// Каждая получает функцию `set(x, y, v)`, где v — множитель яркости (1.0 —
// «как в палитре»). Координаты — пиксели слоя; (u, v) = (x/SIZE, y/SIZE).
//
// ⚠️ Соглашение об осях: у БОКОВЫХ граней v растёт ВВЕРХ (это обеспечивает
// world.js, разворачивая UV). Поэтому «ряды кладки», «полосы карниза» и
// «доски стоймя» ориентированы правильно, а не наискось.

/** Песок: мелкое зерно + редкие камешки. */
function drawSand(set, rnd) {
  const fine = fbmLattices(64, 3, rnd);
  const blot = fbmLattices(4, 2, rnd);
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const u = x / SIZE, v = y / SIZE;
      const g = fbm(u, v, fine, 64, 3);          // зерно
      const b = fbm(u, v, blot, 4, 2);           // крупные наносы
      let m = 1 + (g - 0.5) * 0.16 + (b - 0.5) * 0.13;
      // камешки: редкие тёмные точки, чтобы песок не был однородной кашей
      if (g > 0.86) m *= 0.86;
      set(x, y, m);
    }
  }
}

/** Мощёная дорога: плиты с тёмными швами, каждая своего тона. */
function drawRoad(set, rnd) {
  const N = 5;                       // плит на тайл
  const tone = lattice(N, rnd), tone2 = lattice(N, rnd);
  const grain = fbmLattices(32, 3, rnd);
  const warp = fbmLattices(8, 2, rnd);
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const u = x / SIZE, v = y / SIZE;
      // шов гуляет: идеально прямая сетка читается как линолеум
      const wx = (fbm(u, v, warp, 8, 2) - 0.5) * 0.035;
      const cu = (u + wx) * N, cv = (v - wx) * N;
      // ⚠️ БАГ-УРОК «чёрные трещины в брусчатке». Здесь стояло `Math.floor(cu) % N`.
      // У левого края тайла `u + wx` уходит в МИНУС (шум смещает шов наружу),
      // остаток от деления в JS сохраняет знак, индекс становится −1,
      // `tone[-1]` — undefined, дальше арифметика даёт NaN, а запись NaN в
      // Uint8Array кладёт НОЛЬ. По каждому тайлу шёл чёрный извилистый шов,
      // и выглядело это как трещины в полу — на текстуру никто не думал,
      // подозревали то тени, то дырки в геометрии.
      // ⚠️ Правило: индекс решётки в тайлящейся текстуре приводить к диапазону
      // ВСЕГДА (`((x % N) + N) % N`), даже если «отрицательным он быть не может».
      const iu = ((Math.floor(cu) % N) + N) % N, iv = ((Math.floor(cv) % N) + N) % N;
      const fu = cu - Math.floor(cu), fv = cv - Math.floor(cv);
      const edge = Math.min(fu, 1 - fu, fv, 1 - fv);   // расстояние до шва
      let m = 0.94 + tone[iu] * 0.1 + tone2[iv] * 0.06;
      m += (fbm(u, v, grain, 32, 3) - 0.5) * 0.1;
      // ⚠️ Шов мягкий и узкий намеренно. С затемнением до 0.62 он вблизи
      // читался не как стык плит, а как ТРЕЩИНА в полу — под ноги смотреть
      // становилось тревожнее, чем на противника.
      if (edge < 0.03) m *= 0.78 + edge * 7;
      set(x, y, m);
    }
  }
}

/** Бетон: пятна, потёки, редкая раковина. */
function drawCrete(set, rnd) {
  const big = fbmLattices(4, 3, rnd);
  const fine = fbmLattices(48, 2, rnd);
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const u = x / SIZE, v = y / SIZE;
      const b = fbm(u, v, big, 4, 3);
      const f = fbm(u, v, fine, 48, 2);
      let m = 1 + (b - 0.5) * 0.2 + (f - 0.5) * 0.07;
      if (f > 0.88) m *= 0.9;                    // каверны
      set(x, y, m);
    }
  }
}

/** Песчаная стена: кладка вразбежку, выветренная. */
function drawWall(set, rnd) {
  const ROWS = 4, COLS = 2;                      // на тайл 2 м → кирпич 1×0.5 м
  const tone = lattice(ROWS * COLS * 2, rnd);
  const erode = fbmLattices(24, 3, rnd);
  const big = fbmLattices(3, 2, rnd);
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const u = x / SIZE, v = y / SIZE;
      const row = Math.floor(v * ROWS);
      // вразбежку: нечётный ряд сдвинут на полкирпича
      const off = (row & 1) ? 0.5 : 0;
      const cu = (u * COLS + off) % COLS, cv = v * ROWS;
      const col = Math.floor(cu);
      const fu = cu - col, fv = cv - row;
      const e = fbm(u, v, erode, 24, 3);
      // шов «съеден» шумом — иначе кладка выглядит нарисованной по линейке
      const seam = Math.min(fu * COLS, (1 - fu) * COLS, fv * ROWS, (1 - fv) * ROWS) + (e - 0.5) * 0.5;
      let m = 0.95 + tone[(row * COLS + col) % tone.length] * 0.12;
      m += (e - 0.5) * 0.14 + (fbm(u, v, big, 3, 2) - 0.5) * 0.12;
      if (seam < 0.18) m *= 0.66 + seam * 1.8;
      set(x, y, m);
    }
  }
}

/** Карниз/бордюр: горизонтальный профиль со светлой полкой и тенью под ней. */
function drawTrim(set, rnd) {
  const fine = fbmLattices(32, 3, rnd);
  for (let y = 0; y < SIZE; y++) {
    const v = y / SIZE;
    // профиль: верх светлее (свет ложится на полку), под ним тёмная подрезка
    let p = 1.0;
    if (v > 0.78) p = 1.16;
    else if (v > 0.66) p = 0.74;
    else if (v > 0.2 && v < 0.24) p = 0.82;
    for (let x = 0; x < SIZE; x++) {
      const u = x / SIZE;
      set(x, y, p + (fbm(u, v, fine, 32, 3) - 0.5) * 0.1);
    }
  }
}

/** Скала: крупный рваный шум с трещинами. */
function drawRock(set, rnd) {
  const big = fbmLattices(6, 4, rnd);
  const crack = fbmLattices(10, 3, rnd);
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const u = x / SIZE, v = y / SIZE;
      const b = fbm(u, v, big, 6, 4);
      // «хребтовый» шум: |n-0.5| даёт резкие линии там, где шум переходит через
      // середину — на камне это читается как трещины
      const c = Math.abs(fbm(u, v, crack, 10, 3) - 0.5) * 2;
      let m = 0.94 + (b - 0.5) * 0.34;
      if (c < 0.12) m *= 0.72 + c * 2.2;
      set(x, y, m);
    }
  }
}

/** Дерево: доски стоймя, волокно вдоль доски, тёмные щели между досками. */
function drawWood(set, rnd) {
  const N = 4;                                   // досок на тайл
  const tone = lattice(N, rnd);
  const fibre = fbmLattices(64, 2, rnd);
  const knot = fbmLattices(8, 2, rnd);
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const u = x / SIZE, v = y / SIZE;
      const cu = u * N;
      const pl = Math.floor(cu), fu = cu - pl;
      // волокно вытянуто ВДОЛЬ доски (по v), поэтому по u частота высокая
      const f = fbm(u * 6, v * 0.35, fibre, 64, 2);
      let m = 0.93 + tone[pl % N] * 0.16 + (f - 0.5) * 0.22;
      // сучок
      const k = fbm(u, v, knot, 8, 2);
      if (k > 0.82) m *= 0.78;
      // щель между досками + фаска
      const edge = Math.min(fu, 1 - fu) * N;
      if (edge < 0.09) m *= 0.5 + edge * 3.5;
      else if (edge < 0.2) m *= 1.06;
      set(x, y, m);
    }
  }
}

/** Военный ящик: металлическая панель с рёбрами и заклёпками. */
function drawMetal(set, rnd) {
  const fine = fbmLattices(48, 2, rnd);
  const rust = fbmLattices(6, 3, rnd);
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const u = x / SIZE, v = y / SIZE;
      let m = 1 + (fbm(u, v, fine, 48, 2) - 0.5) * 0.09;
      m += (fbm(u, v, rust, 6, 3) - 0.5) * 0.16;      // потёртости
      // рамка панели
      const bu = Math.min(u, 1 - u), bv = Math.min(v, 1 - v);
      const b = Math.min(bu, bv);
      if (b < 0.045) m *= 0.78;
      else if (b < 0.075) m *= 1.1;
      // заклёпки по рамке
      const rr = 0.06;
      const du = Math.min(Math.abs(u - rr), Math.abs(u - (1 - rr)));
      const dv = Math.min(Math.abs(v - rr), Math.abs(v - (1 - rr)));
      const step = 0.125;
      const nu = Math.abs(((u / step) % 1) - 0.5) * step;
      const nv = Math.abs(((v / step) % 1) - 0.5) * step;
      if ((du < 0.02 && nv < 0.018) || (dv < 0.02 && nu < 0.018)) m *= 1.22;
      set(x, y, m);
    }
  }
}

const DRAW = {
  sand: drawSand, road: drawRoad, crete: drawCrete, wall: drawWall,
  trim: drawTrim, rock: drawRock, wood: drawWood, metal: drawMetal,
};

/**
 * Собрать массив текстур: по слою на каждое имя материала.
 *
 * `names` приходит из `map.json::paint.mats` — порядок задаёт номер слоя,
 * и world.js кладёт этот номер в вершины. Незнакомое имя даёт ровный серый
 * слой: новая карта с новым материалом не должна ронять клиент.
 */
export function makeMaterialTextures(names) {
  const t0 = performance.now();
  const data = new Uint8Array(SIZE * SIZE * names.length);
  for (let i = 0; i < names.length; i++) {
    const base = i * SIZE * SIZE;
    const draw = DRAW[names[i]];
    // ⚠️ Зерно — от ИМЕНИ материала, а не общий счётчик: тогда картинка
    // конкретного материала не меняется от того, что в карте появился новый
    // (иначе после смены карты «поехали» бы все текстуры разом).
    let seed = 0;
    for (const ch of names[i]) seed = (seed * 31 + ch.charCodeAt(0)) | 0;
    const rnd = mulberry32(seed ^ 0x9e3779b9);
    if (!draw) { data.fill(MID, base, base + SIZE * SIZE); continue; }
    draw((x, y, m) => {
      // ⚠️ Проверка на NaN — не перестраховка: запись NaN в Uint8Array молча
      // кладёт ноль, то есть чёрный пиксель, и ищется он потом в тенях и в
      // геометрии, а не в арифметике (см. баг-урок у `drawRoad`).
      const b = Number.isFinite(m) ? Math.round(m * MID) : MID;
      data[base + y * SIZE + x] = b < 0 ? 0 : (b > 255 ? 255 : b);
    }, rnd);
  }

  const tex = new THREE.DataArrayTexture(data, SIZE, SIZE, names.length);
  tex.format = THREE.RedFormat;
  tex.type = THREE.UnsignedByteType;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;                 // 256 — степень двойки, мипы честные
  tex.needsUpdate = true;
  console.log(`[материалы] ${names.length} слоёв ${SIZE}×${SIZE} за ${(performance.now() - t0).toFixed(0)} мс`
    + ` (${Math.round(data.length / 1024)} КБ): ${names.join(', ')}`);
  return tex;
}
